/**
 * Copyright (c) SAGE3 Development Team 2022. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 */

import { Box } from '@chakra-ui/react';
import { MdCropFree } from 'react-icons/md';

import { state as AppState } from './';
import { App, AppGroup } from '../../schema';
import { AppWindow } from '../../components';
import { useUIStore, usePresenceStore, useUser } from '@sage3/frontend';

/**
 * Frame SAGE3 application
 * A contentless rectangle for narrative path overview/revisit/zoom steps
 *
 * Design notes (from CLAUDE.md):
 * - Invisible during delivery (Phase 2: add presentation mode check)
 * - Exempt from progressive reveal (Phase 3: never dim Frame)
 * - Cannot be anchored (Phase 4: exclude from anchor toggle)
 * - Path integrity warnings: already works (AppToolbar checks narrative_order links)
 * - Path panel display: already works (uses app.data.title)
 *
 * @param {AppSchema} props
 * @returns {JSX.Element}
 */
function AppComponent(props: App): JSX.Element {
  // Phase 2: Check presentation mode - Frame is invisible during delivery
  const localPresentationMode = useUIStore((s: any) => s.presentationMode);
  const { user } = useUser();
  const presences = usePresenceStore((s: any) => s.presences);

  // Check if we're in presentation mode:
  // 1. Local presentationMode (if we're the presenter)
  // 2. OR if we're following someone who is in presentation mode (if we're the wall)
  const myPresence = presences.find((p: any) => p._id === user?._id);
  const followingUserId = myPresence?.data?.following;
  const followedUserPresence = followingUserId
    ? presences.find((p: any) => p._id === followingUserId)
    : null;
  const followedUserInPresentation = followedUserPresence?.data?.presentationMode || false;

  const presentationMode = localPresentationMode || followedUserInPresentation;

  // In delivery mode, render nothing (Frame is invisible)
  if (presentationMode) {
    // Return minimal invisible container to preserve layout
    // The viewport will still target Frame's position/size for navigation
    return (
      <AppWindow app={props} hideBackgroundIcon={MdCropFree} background={false} pointerEventsNone={true}>
        <Box w="100%" h="100%" backgroundColor="transparent" pointerEvents="none" />
      </AppWindow>
    );
  }

  // In authoring mode, show dashed outline with 3×6 grid
  return (
    <AppWindow app={props} hideBackgroundIcon={MdCropFree} background={false} pointerEventsNone={true}>
      <Box
        w="100%"
        h="100%"
        border="3px dashed"
        borderColor="orange.400"
        borderRadius="md"
        backgroundColor="rgba(255, 255, 255, 0.03)"
        pointerEvents="none"
        position="relative"
      >
        {/* 3×6 Grid overlay (3 rows, 6 columns) */}
        <Box position="absolute" top="0" left="0" w="100%" h="100%" pointerEvents="none">
          {/* Vertical lines (5 lines for 6 columns) */}
          {[1, 2, 3, 4, 5].map((i) => (
            <Box
              key={`v${i}`}
              position="absolute"
              left={`${(i * 100) / 6}%`}
              top="0"
              bottom="0"
              w="2px"
              backgroundColor="orange.400"
              opacity="0.8"
              boxShadow="0 0 2px rgba(0,0,0,0.3)"
            />
          ))}
          {/* Horizontal lines (2 lines for 3 rows) */}
          {[1, 2].map((i) => (
            <Box
              key={`h${i}`}
              position="absolute"
              top={`${(i * 100) / 3}%`}
              left="0"
              right="0"
              h="2px"
              backgroundColor="orange.400"
              opacity="0.8"
              boxShadow="0 0 2px rgba(0,0,0,0.3)"
            />
          ))}
        </Box>
      </Box>
    </AppWindow>
  );
}

/**
 * Toolbar component for Frame app
 * Minimal toolbar - Frame has no interactive controls
 */
function ToolbarComponent(_props: App): JSX.Element {
  // Frame has no controls - return empty fragment
  return <></>;
}

/**
 * Grouped App toolbar component
 * Displayed when multiple Frame apps are selected
 */
const GroupedToolbarComponent = (_props: { apps: AppGroup }) => {
  // No grouped controls needed for Frame
  return <></>;
};

export default { AppComponent, ToolbarComponent, GroupedToolbarComponent };
